import apiClient from './api.service';
import { AdminEngagementWinnerContact } from '../types/engagement.types';

interface WrappedResponse<T> {
  data: T;
}

interface CyclePayload {
  cycleKey?: string;
  newCycleKey?: string;
  startsAt?: string;
  endsAt?: string;
  timezone?: string;
  surveyPoints?: number;
  referralPoints?: number;
  minimumPrizePoints?: number;
  lotteryDrawNumber?: string;
  lotteryDrawDate?: string;
  spaRegistrationNumber?: string;
}

interface PrizePayload {
  prizeRank: number;
  prizeValue: string;
}

class EngagementAdminService {
  async listCycles(params?: { status?: string; page?: number; limit?: number }) {
    const response = await apiClient.get<WrappedResponse<any>>(
      '/api/admin/engagement/cycles',
      { params }
    );
    return response.data.data;
  }

  async getCycleDetail(cycleKey: string) {
    const response = await apiClient.get<WrappedResponse<any>>(
      `/api/admin/engagement/cycles/${cycleKey}`
    );
    return response.data.data;
  }

  async createCycle(payload: CyclePayload) {
    const response = await apiClient.post<WrappedResponse<any>>(
      '/api/admin/engagement/cycles',
      payload
    );
    return response.data.data;
  }

  async updateCycle(cycleKey: string, payload: Partial<CyclePayload>) {
    const response = await apiClient.patch<WrappedResponse<any>>(
      `/api/admin/engagement/cycles/${cycleKey}`,
      payload
    );
    return response.data.data;
  }

  async closeCycle(cycleKey: string, options?: { forceReprocess?: boolean; reason?: string }) {
    const response = await apiClient.post<WrappedResponse<any>>(
      `/api/admin/engagement/cycles/${cycleKey}/close`,
      options ?? {}
    );
    return response.data.data;
  }

  async setPrizes(cycleKey: string, prizes: PrizePayload[]) {
    const response = await apiClient.put<WrappedResponse<any>>(
      `/api/admin/engagement/cycles/${cycleKey}/prizes`,
      { prizes }
    );
    return response.data.data;
  }

  async processWinners(cycleKey: string, drawnNumbers: string[]) {
    const response = await apiClient.post<WrappedResponse<any>>(
      `/api/admin/engagement/cycles/${cycleKey}/process-winners`,
      { drawnNumbers }
    );
    return response.data.data;
  }

  async inactivateCycle(cycleKey: string) {
    const response = await apiClient.post<WrappedResponse<any>>(
      `/api/admin/engagement/cycles/${cycleKey}/inactivate`
    );
    return response.data.data;
  }

  async reactivateCycle(cycleKey: string) {
    const response = await apiClient.post<WrappedResponse<any>>(
      `/api/admin/engagement/cycles/${cycleKey}/reactivate`
    );
    return response.data.data;
  }

  async getWinnersContactDetails(cycleKey: string) {
    const response = await apiClient.get<WrappedResponse<AdminEngagementWinnerContact[]>>(
      `/api/admin/engagement/cycles/${cycleKey}/winners`
    );
    return response.data.data;
  }

  async getCycleReport(cycleKey: string) {
    const response = await apiClient.get<WrappedResponse<any>>(
      `/api/admin/engagement/cycles/${cycleKey}/report`
    );
    return response.data.data;
  }

  async getRegulation() {
    const response = await apiClient.get<WrappedResponse<any>>(
      '/api/admin/engagement/regulation'
    );
    return response.data.data;
  }

  async publishRegulation(content: string) {
    const response = await apiClient.put<WrappedResponse<any>>(
      '/api/admin/engagement/regulation',
      { content }
    );
    return response.data.data;
  }
}

export const engagementAdminService = new EngagementAdminService();
